import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, rules } from '@cwos/api-client';
import { ConsequenceNote } from '@cwos/ui-admin';
import { SettingsCard } from './common.js';
import {
  asNumber,
  categoryLabel,
  paramKind,
  paramLabel,
  scheduleOptions,
} from './rulesVocabulary.js';
import {
  namespacedRuleType,
  withoutAudienceParams,
  TIER_LABEL,
  type AuthorityTier,
} from './automationTiers.js';

type Automation = rules.AutomationView;
type CatalogEntry = rules.RuleCatalogEntry;

/**
 * The WHEN / IF / DO automation builder (owner directive, product priority E),
 * over the rules engine's converged create service.
 *
 * WHEN — a rule type from the LIVE catalog (`GET /rules/catalog`; the engine's
 * code-defined modules, never an invented type) plus its schedule. IF — that
 * type's parameters, rendered from the catalog's `defaultParams` shapes rather
 * than a hardcoded per-type form. DO — severity, notification, escalation:
 * the action surface the engine actually has.
 *
 * The authority tier is the FIRST question, because it decides everything the
 * later steps may offer (docs/AUTOMATION-CAPABILITY-MODEL.md):
 *
 *   personal     — reaches its owner ONLY, structurally. The audience-shaped
 *                  controls (supervisor notification, escalation, audience
 *                  parameters) are ABSENT here, mirroring the server's shared
 *                  refusal (`personalAudienceRefusals`) — absent, not grayed
 *                  out, because a control the server will refuse is not a
 *                  control.
 *   team         — reaches the workers the owner supervises at the moment it
 *                  fires; the live edge is re-checked on every run and the
 *                  step says so honestly.
 *   organization — today's org rules, unchanged. Rule types are unique per
 *                  org, so an organization tier pick of a type that already
 *                  has its row CONFIGURES that row (PATCH) instead of
 *                  attempting a create the server would 409.
 *
 * Personal and team creates go to `POST /api/rules` with the rule type
 * namespaced per owner (`{tier}.{catalogType}.{ownerUserId}`), invisibly: the
 * person sees and names the automation in plain language; the namespaced key
 * appears only in the advanced disclosure.
 */
export function AutomationBuilder({
  catalog,
  automations,
  tiers,
  ownerUserId,
  onDone,
  onCancel,
}: {
  catalog: CatalogEntry[];
  automations: Automation[];
  /** The tiers THIS caller may create — a tier not held is not rendered. */
  tiers: AuthorityTier[];
  ownerUserId: string;
  /** Called with the created/updated rule id after a successful save. */
  onDone: (ruleId: string) => void;
  onCancel: () => void;
}) {
  const qc = useQueryClient();
  const [tier, setTier] = useState<AuthorityTier | null>(tiers.length === 1 ? tiers[0]! : null);
  const [type, setType] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [step, setStep] = useState<'tier' | 'when' | 'conditions'>(
    tiers.length === 1 ? 'when' : 'tier',
  );
  const [parameters, setParameters] = useState<Record<string, unknown>>({});
  const [removedParams, setRemovedParams] = useState<string[]>([]);
  const [severity, setSeverity] = useState<rules.RuleSeverity>('warning');
  const [scheduleConfig, setScheduleConfig] = useState<Record<string, unknown>>({});
  const [notifyWorker, setNotifyWorker] = useState(false);
  const [notifySupervisor, setNotifySupervisor] = useState(false);
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalationAfterMinutes, setEscalationAfterMinutes] = useState(60);

  const selected = catalog.find((c) => c.type === type) ?? null;

  /**
   * Rule types are unique per organization (`workflow_rule_type_org_uk`), so
   * when the ORGANIZATION tier picks a type whose row already exists, saving
   * CONFIGURES that row — the create path would only 409. This is exactly
   * today's org-rule behavior, kept.
   */
  const orgExisting =
    tier === 'organization' && selected
      ? automations.find((a) => a.ruleType === selected.type && a.authorityTier === 'organization')
      : undefined;

  const categories = useMemo(() => {
    const seen = new Map<string, CatalogEntry[]>();
    for (const c of catalog) {
      const key = c.triggerCategory ?? 'other';
      const list = seen.get(key) ?? [];
      list.push(c);
      seen.set(key, list);
    }
    return [...seen.entries()];
  }, [catalog]);

  const templates = useMemo(
    () => AUTOMATION_TEMPLATES.filter((t) => catalog.some((c) => c.type === t.baseType)),
    [catalog],
  );

  const create = useMutation({
    mutationFn: (input: rules.CreateTierScopedRuleInput) => rules.create(input),
    onSuccess: (rule) => {
      void qc.invalidateQueries({ queryKey: ['rules'] });
      onDone(rule.id);
    },
  });
  const configure = useMutation({
    mutationFn: (input: { id: string; body: rules.RulePatch }) =>
      rules.update(input.id, input.body),
    onSuccess: (rule) => {
      void qc.invalidateQueries({ queryKey: ['rules'] });
      onDone(rule.id);
    },
  });
  const saving = create.isPending || configure.isPending;
  const saveError = create.error ?? configure.error;

  /** Pre-fill the builder from a catalog entry, optionally through a template. */
  const startFrom = (entry: CatalogEntry, template?: AutomationTemplate) => {
    if (tier === null) return;
    const base = { ...entry.defaultParams, ...(template?.params ?? {}) };
    // A personal automation's audience is its owner, structurally: the server
    // REFUSES audience parameters (never strips them), so the builder never
    // offers or sends one. `removedParams` lets the conditions step say when a
    // catalog default was withheld for that reason.
    const scoped =
      tier === 'personal' ? withoutAudienceParams(base) : { params: base, removed: [] };
    setType(entry.type);
    setName(template?.title ?? entry.name);
    setParameters(scoped.params);
    setRemovedParams(scoped.removed);
    setSeverity((template?.severity ?? entry.defaultSeverity) as rules.RuleSeverity);
    setScheduleConfig({ ...(template?.schedule ?? entry.defaultSchedule) });
    setNotifyWorker(template?.notifyWorker ?? false);
    setNotifySupervisor(tier !== 'personal' && (template?.notifySupervisor ?? false));
    setEscalationEnabled(tier !== 'personal' && template?.escalationAfterMinutes !== undefined);
    setEscalationAfterMinutes(template?.escalationAfterMinutes ?? 60);
    create.reset();
    configure.reset();
  };

  const setParam = (key: string, value: unknown) => setParameters((p) => ({ ...p, [key]: value }));

  const save = () => {
    if (!tier || !selected) return;
    const notificationConfig: rules.NotificationConfig =
      tier === 'personal' ? { notifyWorker } : { notifyWorker, notifySupervisor };
    if (orgExisting) {
      configure.mutate({
        id: orgExisting.id,
        body: {
          enabled: true,
          parameters,
          severity,
          scheduleConfig,
          notificationConfig,
          escalationConfig: escalationEnabled
            ? { enabled: true, afterMinutes: escalationAfterMinutes }
            : { enabled: false },
        },
      });
      return;
    }
    const input: rules.CreateTierScopedRuleInput = {
      authorityTier: tier,
      ruleType: namespacedRuleType(tier, selected.type, ownerUserId),
      name: name.trim() || selected.name,
      enabled: true,
      parameters,
      severity,
      scheduleConfig,
      notificationConfig,
    };
    // Escalation reaches a supervisor, so a personal automation cannot carry
    // it — the server refuses the whole config; the builder never builds one.
    if (tier !== 'personal' && escalationEnabled) {
      input.escalationConfig = { enabled: true, afterMinutes: escalationAfterMinutes };
    }
    create.mutate(input);
  };

  const storedType = tier && selected ? namespacedRuleType(tier, selected.type, ownerUserId) : null;

  return (
    <SettingsCard>
      <div className="rn-card-head">
        <h2 className="rn-card-title">Create automation</h2>
        <button type="button" className="rn-link" onClick={onCancel}>
          Cancel
        </button>
      </div>

      {step === 'tier' ? (
        <div>
          <p className="rn-muted" style={{ marginTop: 0 }}>
            Who is this automation for? That decides who it may ever reach.
          </p>
          <div
            role="radiogroup"
            aria-label="Who is this automation for?"
            style={{ display: 'grid', gap: 'var(--space-8)' }}
          >
            {tiers.map((t) => (
              <button
                key={t}
                type="button"
                role="radio"
                aria-checked={tier === t}
                className="rn-card"
                style={{ textAlign: 'left', cursor: 'pointer' }}
                onClick={() => setTier(t)}
              >
                <span style={{ fontWeight: 600 }}>{TIER_TITLE[t]}</span>
                <span className="rn-muted" style={{ display: 'block', fontSize: 13 }}>
                  {TIER_BLURB[t]}
                </span>
              </button>
            ))}
          </div>
          <div style={{ marginTop: 'var(--space-12)' }}>
            <button
              type="button"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
              disabled={tier === null}
              onClick={() => setStep('when')}
            >
              Continue
            </button>
          </div>
        </div>
      ) : null}

      {step === 'when' && tier !== null ? (
        <div>
          {tiers.length > 1 ? (
            <p className="rn-muted" style={{ marginTop: 0 }}>
              {TIER_TITLE[tier]}{' '}
              <button type="button" className="rn-link" onClick={() => setStep('tier')}>
                Change
              </button>
            </p>
          ) : (
            <p className="rn-muted" style={{ marginTop: 0 }}>
              {TIER_TITLE[tier]} — {TIER_BLURB[tier]}
            </p>
          )}
          <p style={{ marginTop: 0 }}>
            <strong>WHEN</strong> — choose what this automation watches for.
          </p>

          {templates.length > 0 ? (
            <div style={{ marginBottom: 'var(--space-12)' }}>
              <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
                Start from a template
              </span>
              <div style={{ display: 'grid', gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
                {templates.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className="rn-card"
                    style={{ textAlign: 'left', cursor: 'pointer' }}
                    onClick={() => {
                      const entry = catalog.find((c) => c.type === t.baseType);
                      if (!entry) return;
                      startFrom(entry, t);
                      setStep('conditions');
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{t.title}</span>
                    <span className="rn-muted" style={{ display: 'block', fontSize: 13 }}>
                      {t.blurb}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
            Or pick a trigger
          </span>
          <div style={{ display: 'grid', gap: 'var(--space-12)', marginTop: 'var(--space-8)' }}>
            {categories.map(([cat, entries]) => (
              <div key={cat}>
                <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
                  {categoryLabel(cat)}
                </span>
                <div
                  style={{ display: 'grid', gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}
                >
                  {entries.map((c) => (
                    <button
                      key={c.type}
                      type="button"
                      className="rn-card"
                      style={{ textAlign: 'left', cursor: 'pointer' }}
                      onClick={() => {
                        startFrom(c);
                        setStep('conditions');
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                      <span className="rn-muted" style={{ display: 'block', fontSize: 13 }}>
                        {c.triggerConcept ?? c.description}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {step === 'conditions' && tier !== null && selected ? (
        <div>
          <p style={{ marginTop: 0 }}>
            <strong>WHEN</strong> — {selected.triggerConcept ?? selected.description}{' '}
            <button
              type="button"
              className="rn-link"
              onClick={() => {
                setType(null);
                setStep('when');
              }}
            >
              Change
            </button>
          </p>

          <div style={{ marginTop: 'var(--space-8)' }}>
            <label style={{ display: 'block' }}>
              <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
                How often it runs
              </span>
              <select
                value={JSON.stringify(scheduleConfig ?? {})}
                onChange={(e) => setScheduleConfig(JSON.parse(e.target.value))}
              >
                {scheduleOptions(scheduleConfig ?? {}).map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {Object.keys(parameters).length > 0 || removedParams.length > 0 ? (
            <div style={{ marginTop: 'var(--space-16)' }}>
              <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
                IF — conditions
              </span>
              {removedParams.length > 0 ? (
                <p className="rn-muted" style={{ fontSize: 12, margin: 'var(--space-6) 0 0' }}>
                  Not offered here: {removedParams.map(paramLabel).join(', ')}. A personal
                  automation reaches only you, so it takes no audience settings.
                </p>
              ) : null}
              <div style={{ display: 'grid', gap: 'var(--space-10)', marginTop: 'var(--space-8)' }}>
                {Object.keys(parameters).map((key) => {
                  const def =
                    key in selected.defaultParams ? selected.defaultParams[key] : parameters[key];
                  const kind = paramKind(def);
                  const current = parameters[key];
                  return (
                    <label key={key} style={{ display: 'block' }}>
                      <span style={{ fontWeight: 600 }}>{paramLabel(key)}</span>
                      {kind === 'number' ? (
                        <input
                          type="number"

                          value={current === undefined ? asNumber(def) : (current as number)}
                          onChange={(e) => setParam(key, Number(e.target.value))}
                        />
                      ) : (
                        <input
                          type="text"

                          value={current === undefined ? String(def) : String(current)}
                          onChange={(e) => setParam(key, e.target.value)}
                        />
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 'var(--space-16)' }}>
            <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
              DO — what happens on a match
            </span>
            <div style={{ display: 'grid', gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
              <span className="rn-muted" style={{ fontSize: 12 }}>
                A match always raises an exception in Needs attention.
              </span>
              <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={notifyWorker}
                  onChange={(e) => setNotifyWorker(e.target.checked)}
                />
                {tier === 'personal' ? 'Notify me' : 'Notify the worker it’s about'}
              </label>
              {tier !== 'personal' ? (
                <>
                  <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={notifySupervisor}
                      onChange={(e) => setNotifySupervisor(e.target.checked)}
                    />
                    Notify a supervisor
                  </label>
                  <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={escalationEnabled}
                      onChange={(e) => setEscalationEnabled(e.target.checked)}
                    />
                    Escalate if it stays unresolved
                  </label>
                  {escalationEnabled ? (
                    <label style={{ display: 'flex', gap: 'var(--space-8)', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600 }}>Escalate after</span>
                      <input
                        type="number"
                        min={1}

                        style={{ width: 96 }}
                        value={escalationAfterMinutes}
                        onChange={(e) =>
                          setEscalationAfterMinutes(
                            Math.max(1, Math.floor(Number(e.target.value) || 1)),
                          )
                        }
                      />
                      <span className="rn-muted">minutes</span>
                    </label>
                  ) : null}
                </>
              ) : (
                <span className="rn-muted" style={{ fontSize: 12 }}>
                  This reaches only you, so there is nothing here about supervisors or escalation —
                  those belong to team and organization automations.
                </span>
              )}
            </div>

            <div style={{ marginTop: 'var(--space-12)' }}>
              <span className="rn-muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>
                Severity
              </span>
              <div style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-8)' }}>
                {(['info', 'warning', 'critical'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    className="rn-link"
                    aria-pressed={severity === s}
                    onClick={() => setSeverity(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {orgExisting ? (
              <p className="rn-muted" style={{ marginTop: 'var(--space-12)' }}>
                Your organization already has “{orgExisting.name}” for this trigger — there is one
                organization rule per trigger, so saving updates it.
              </p>
            ) : (
              <div style={{ marginTop: 'var(--space-12)' }}>
                <label style={{ display: 'block' }}>
                  <span style={{ fontWeight: 600 }}>Name</span>
                  <input
                    type="text"

                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </label>
              </div>
            )}

            <p className="rn-muted" style={{ fontSize: 12, marginTop: 'var(--space-12)' }}>
              {tier === 'personal'
                ? 'Created for you alone. It reaches only you — that is built in, not a setting.'
                : tier === 'team'
                  ? 'Reaches the workers you supervise at the moment it runs. That is checked again every time it fires, and it pauses on its own if you lose that authority.'
                  : 'An organization rule — it behaves exactly like the rules on this page always have.'}
            </p>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-8)', marginTop: 'var(--space-16)' }}>
            <button
              type="button"
              className="rn-link"
              onClick={() => {
                setType(null);
                setStep('when');
              }}
            >
              Back
            </button>
            <button
              type="button"
              className="signin-devbtn"
              style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : orgExisting ? 'Save to organization rule' : 'Create & turn on'}
            </button>
          </div>

          {saveError ? (
            <p role="alert" style={{ color: 'var(--color-urgent-deep)' }}>
              {describeSaveError(saveError, tier)}
            </p>
          ) : null}

          <details style={{ marginTop: 'var(--space-8)' }}>
            <summary className="rn-muted" style={{ fontSize: 12, cursor: 'pointer' }}>
              Advanced
            </summary>
            <p className="rn-muted" style={{ fontSize: 12 }}>
              This maps onto the code-defined rule module “{selected.type}”. You configure it; the
              engine runs it.{' '}
              {storedType !== selected.type
                ? `It is stored under the key “${storedType}” — rule types are unique per organization, so a ${TIER_LABEL[tier].toLowerCase()} automation carries its owner in the key.`
                : ''}
            </p>
          </details>
          <ConsequenceNote text="It won’t run until the scheduler tick or a manual Run now." />
        </div>
      ) : null}
    </SettingsCard>
  );
}

const TIER_TITLE: Record<AuthorityTier, string> = {
  personal: 'Just for me',
  team: 'My team',
  organization: 'The whole organization',
};

const TIER_BLURB: Record<AuthorityTier, string> = {
  personal: 'A reminder that reaches only you. Nobody else is ever notified.',
  team: 'Reaches the workers you supervise at the moment it runs — re-checked every time it fires.',
  organization:
    'An organization rule, watching per its trigger — what this page has always managed.',
};

/**
 * Why a save was refused, in words a person can act on. The server is the
 * authority; this only translates its shapes (409 type conflict, 403 tier
 * authority, 400 validation) without softening them.
 */
export function describeSaveError(err: unknown, tier: AuthorityTier): string {
  if (err instanceof ApiError) {
    if (err.status === 409) {
      return tier === 'organization'
        ? 'The organization already has an automation of this type. It is in the list — edit it there.'
        : 'You already have an automation of this type. Edit or remove that one instead of creating a second.';
    }
    if (err.status === 403) {
      return `The server refused: you don’t have the permission this tier needs. ${err.message}`;
    }
    if (err.status === 400) {
      const issues = (err.body as { issues?: { path: string; message: string }[] } | null)?.issues;
      if (issues && issues.length > 0) {
        return `The server refused this configuration: ${issues.map((i) => i.message).join(' ')}`;
      }
      return `The server refused this configuration: ${err.message}`;
    }
  }
  return 'Saving failed. Nothing was created — try again.';
}

/**
 * Starter templates (product priority F): each is nothing but a PRE-FILLED
 * builder state over a REAL catalog type — the type must be present in the
 * live catalog or the template is not offered, so a template can never invent
 * a rule the engine does not have code for.
 */
export interface AutomationTemplate {
  key: string;
  title: string;
  blurb: string;
  /** A rule type that must exist in the live catalog. */
  baseType: string;
  params?: Record<string, unknown>;
  severity?: rules.RuleSeverity;
  schedule?: Record<string, unknown>;
  notifyWorker?: boolean;
  notifySupervisor?: boolean;
  escalationAfterMinutes?: number;
}

export const AUTOMATION_TEMPLATES: readonly AutomationTemplate[] = Object.freeze([
  {
    key: 'forgotten-clock-outs',
    title: 'Catch forgotten clock-outs',
    blurb: 'A shift left open past 12 hours raises an exception and tells the worker.',
    baseType: 'missing_clock_out',
    params: { maxOpenHours: 12 },
    severity: 'warning',
    notifyWorker: true,
  },
  {
    key: 'unsubmitted-timesheets',
    title: 'Chase unsubmitted timesheets',
    blurb:
      'Every Monday morning, flag timesheets past their deadline — worker and supervisor both hear about it.',
    baseType: 'timesheet_not_submitted',
    schedule: { kind: 'cron', expression: '0 8 * * 1' },
    notifyWorker: true,
    notifySupervisor: true,
  },
  {
    key: 'visits-without-time',
    title: 'Visits without a time record',
    blurb: 'A service visit with no matching clock time gets flagged to a supervisor daily.',
    baseType: 'service_event_without_time_record',
    severity: 'warning',
    schedule: { kind: 'cron', expression: '0 9 * * *' },
    notifySupervisor: true,
  },
  {
    key: 'stale-documentation',
    title: 'Escalate stale documentation',
    blurb:
      'Documentation open past its deadline tells the worker, then escalates after 4 hours unresolved.',
    baseType: 'documentation_open_past_threshold',
    severity: 'warning',
    notifyWorker: true,
    escalationAfterMinutes: 240,
  },
  {
    key: 'retired-references',
    title: 'Schedule points at something retired',
    blurb:
      'An upcoming shift still booked against a retired program or site is flagged every morning.',
    baseType: 'retired_reference_in_future_schedule',
    severity: 'warning',
    schedule: { kind: 'cron', expression: '0 8 * * *' },
  },
]);
